import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  useParams,
  type AnyRoute,
} from '@tanstack/react-router'
import { AppShell } from './components/AppShell'
import { PAGES } from './lib/pages'
import { PAGE_COMPONENTS } from './routes/pages'
import { GeraetDetailPage } from './routes/GeraetDetail'
import { KontoPage } from './routes/Konto'

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: NotFound,
})

function NotFound() {
  return (
    <div className="content">
      <h1 className="page-title">Seite nicht gefunden</h1>
      <p className="muted">Die aufgerufene Adresse gibt es nicht.</p>
      <p>
        <a href="/">Zur Übersicht</a>
      </p>
    </div>
  )
}

/** Layout für "Alle Kunden" – kein Präfix in der URL. */
const globalLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: 'global',
  component: () => <AppShell customerSlug={null} />,
})

/** Layout im Kundenkontext – /k/<kunde-slug>/… */
const customerLayout = createRoute({
  getParentRoute: () => rootRoute,
  path: '/k/$customerSlug',
  component: CustomerLayout,
})

function CustomerLayout() {
  const { customerSlug } = useParams({ from: '/k/$customerSlug' })
  return <AppShell customerSlug={customerSlug} />
}

// Jede Seite existiert zweimal: kundenübergreifend und im Kundenkontext.
// Beide Bäume werden aus derselben Seitenliste erzeugt, damit sie nicht
// auseinanderlaufen.
const globalPages: AnyRoute[] = PAGES.map((page) =>
  createRoute({
    getParentRoute: () => globalLayout,
    path: page.segment ? `/${page.segment}` : '/',
    component: () => {
      const Component = PAGE_COMPONENTS[page.segment]!
      return <Component customerSlug={null} />
    },
  }),
)

const customerPages: AnyRoute[] = PAGES.map((page) =>
  createRoute({
    getParentRoute: () => customerLayout,
    path: page.segment ? `/${page.segment}` : '/',
    component: function CustomerPage() {
      const { customerSlug } = useParams({ from: '/k/$customerSlug' })
      const Component = PAGE_COMPONENTS[page.segment]!
      return <Component customerSlug={customerSlug} />
    },
  }),
)

// Gerätedetail gibt es in beiden Kontexten.
const globalDeviceRoute = createRoute({
  getParentRoute: () => globalLayout,
  path: '/geraete/$deviceId',
  component: function GlobalDevice() {
    const { deviceId } = useParams({ from: '/global/geraete/$deviceId' })
    return <GeraetDetailPage deviceId={deviceId} customerSlug={null} />
  },
})

const customerDeviceRoute = createRoute({
  getParentRoute: () => customerLayout,
  path: '/geraete/$deviceId',
  component: function CustomerDevice() {
    const { deviceId, customerSlug } = useParams({ from: '/k/$customerSlug/geraete/$deviceId' })
    return <GeraetDetailPage deviceId={deviceId} customerSlug={customerSlug} />
  },
})

// Das eigene Konto hängt nicht am Kundenkontext.
const kontoRoute = createRoute({
  getParentRoute: () => globalLayout,
  path: '/konto',
  component: KontoPage,
})

export const routeTree = rootRoute.addChildren([
  globalLayout.addChildren([...globalPages, globalDeviceRoute, kontoRoute]),
  customerLayout.addChildren([...customerPages, customerDeviceRoute]),
])

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
